import React from 'react'
import isEmpty from 'lodash/isEmpty'
import type { Blog } from '@ztechapps/common.contract'
import { truncate } from '@ztechapps/common.utils'
import { Alert, Box, ShowIf } from '@ztechapps/webui.util'
import { BusyButton } from '@ztechapps/webui.button'
import { useAiChatContext } from '../ai-chat/Context'

export type BlogResultListProps = {
  posts: Blog[]
}

const SUMMARY_MAX = 180

/**
 * Renders a list of blog posts returned by the AI chat `findBlogPosts` tool
 * as a compact list. URL construction is delegated per-surface via the
 * optional `blogUrl` builder on `AiChatProvider`.
 */
export const BlogResultList = ({ posts }: BlogResultListProps) => {
  const { blogUrl } = useAiChatContext()
  const resolveUrl = blogUrl ?? (() => '#')

  if (isEmpty(posts)) {
    return (
      <Alert type="info">
        <span className="text-body">No articles matched your query.</span>
      </Alert>
    )
  }

  return (
    <div className="d-flex flex-column gap-2">
      {posts.map((post) => {
        const url = resolveUrl(post)
        const title = post.title ?? 'Untitled article'
        const summary = truncate(post.description ?? '', SUMMARY_MAX)

        return (
          <Box key={post._id} className="bg-body">
            <div className="d-flex flex-column gap-2">
              <div className="fw-semibold text-body">{title}</div>

              <ShowIf condition={!isEmpty(summary)}>
                <div className="small text-body-secondary">{summary}</div>
              </ShowIf>

              <div>
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-decoration-none"
                  aria-label={`Read article ${title}`}
                >
                  <BusyButton variant="outline-primary" bsSize="sm" text="Read article" />
                </a>
              </div>
            </div>
          </Box>
        )
      })}
    </div>
  )
}
